import { StatusPill, Tabs, Badge, Card, CardContent } from '@insightai/ui'
import { AIBadge, DisclosureLabel } from '@insightai/ai-patterns'

/**
 * M00-07 — tabbed feature showcase.
 *
 * The five tabs are the ones named in §8: Decomposition · Synthesis Desk ·
 * Fieldwork Automation · Crosstabs AI · Provenance Report.
 *
 * The panels use the REAL design-system components — StatusPill, AIBadge,
 * DisclosureLabel — rather than screenshots. That keeps the marketing site
 * honest as the product evolves (a changed disclosure string updates here too),
 * and it means the landing page cannot advertise a treatment the product does not
 * actually ship.
 */
const TABS = [
  {
    value: 'decomposition',
    label: 'Decomposition',
    heading: 'From a business question to a MECE issue tree',
    body: 'TOSCA framing extracts the trouble, owner, success criteria, constraints and actors, then decomposes the question into mutually exclusive branches — including a null hypothesis, always.',
    panel: (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="brand">Hypothesis</Badge>
          <Badge tone="verified">Argument</Badge>
          <Badge tone="warning">Sub-argument</Badge>
          <Badge tone="accent">Evidence</Badge>
        </div>
        <p className="text-body-sm text-text-secondary">
          Research readiness is scored before submission, so a vague brief is refined rather than
          fielded.
        </p>
        <div className="flex flex-wrap gap-2">
          <StatusPill tone="verified" label="Ready — 82" size="sm" />
          <StatusPill tone="warning" label="Needs refinement — 61" size="sm" />
          <StatusPill tone="critical" label="Too vague — 38" size="sm" />
        </div>
      </div>
    ),
  },
  {
    value: 'synthesis',
    label: 'Synthesis Desk',
    heading: 'Desk research that shows its working',
    body: 'Public and licensed sources are queried, evaluated and synthesised under strict RAG: every claim maps to a retrievable passage, with publication and retrieval dates kept distinct.',
    panel: (
      <div className="flex flex-col gap-3">
        <p className="text-body-sm text-text-primary">
          Urban penetration of packaged snacks reached 74% in 2025
          <sup className="ms-0.5 text-caption font-semibold text-text-link">1</sup>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone="verified" label="Authoritative · 0.86" size="sm" />
          <Badge tone="info">Client-provided</Badge>
          <Badge tone="warning">Licensed source</Badge>
        </div>
        <p className="text-caption text-text-secondary">
          Where sources disagree, both are shown side by side and neither is marked correct.
        </p>
      </div>
    ),
  },
  {
    value: 'fieldwork',
    label: 'Fieldwork Automation',
    heading: 'Live fieldwork you can trust to be current',
    body: 'Response counters, quality-pass rates and quota completion update within three seconds of each response. If the stream drops, the dashboard says so rather than quietly freezing.',
    panel: (
      <div className="flex flex-col gap-3">
        <div className="flex items-baseline gap-2">
          <span className="text-display tabular-nums">847</span>
          <span className="text-body-sm text-text-secondary">of 1,200 responses</span>
        </div>
        <div
          role="img"
          aria-label="Fieldwork progress: 847 of 1,200 responses, about 71 percent"
          className="h-2 w-full overflow-hidden rounded-full bg-surface-inset"
        >
          <div className="h-full rounded-full bg-brand-solid" style={{ width: '71%' }} />
        </div>
        <StatusPill tone="warning" label="Reconnecting — figures may lag" size="sm" />
      </div>
    ),
  },
  {
    value: 'crosstabs',
    label: 'Crosstabs AI',
    heading: 'Significance testing without a stats degree',
    body: 'Cross-tabulations carry A/B/C letter notation at 95% confidence, driver importance is ranked with R² and p-values, and verbatims are themed and anonymised to a demographic profile.',
    panel: (
      <table className="w-full border-collapse text-body-sm">
        <caption className="pb-2 text-start text-caption text-text-secondary">
          Purchase intent by age band, letters mark significant differences at 95% CI
        </caption>
        <thead>
          <tr className="text-caption uppercase tracking-wide text-text-tertiary">
            <th scope="col" className="py-1 text-start">Segment</th>
            <th scope="col" className="py-1 text-end">18–24 (A)</th>
            <th scope="col" className="py-1 text-end">25–34 (B)</th>
            <th scope="col" className="py-1 text-end">35+ (C)</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-border-subtle">
            <th scope="row" className="py-1.5 text-start font-normal">Definitely would buy</th>
            <td className="py-1.5 text-end tabular-nums">34% <span className="text-text-tertiary">C</span></td>
            <td className="py-1.5 text-end tabular-nums">31% <span className="text-text-tertiary">C</span></td>
            <td className="py-1.5 text-end tabular-nums">19%</td>
          </tr>
        </tbody>
      </table>
    ),
  },
  {
    value: 'provenance',
    label: 'Provenance Report',
    heading: 'A report that survives scrutiny',
    body: 'Every page carries an AI disclosure label, each finding links back to the problem statement, and the appendix documents governance, provenance, scale performance and data quality.',
    panel: (
      <div className="flex flex-col gap-3">
        <AIBadge source={{ model: 'llama-3.1-70b', version: '1.0', generationId: 'gen_demo' }} />
        <DisclosureLabel kind="aiDisclosure" />
        <p className="text-caption text-text-secondary">
          These are the actual components used in the product — not mock-ups of them.
        </p>
      </div>
    ),
  },
]

export function FeatureShowcase() {
  return (
    <section aria-labelledby="showcase-heading" className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
      <h2 id="showcase-heading" className="text-h1">
        What runs under the hood
      </h2>

      <div className="pt-8">
        <Tabs
          label="Platform capabilities"
          items={TABS.map((tab) => ({
            value: tab.value,
            label: tab.label,
            content: (
              <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                <div className="flex flex-col gap-3">
                  <h3 className="text-h2">{tab.heading}</h3>
                  <p className="text-body text-text-secondary">{tab.body}</p>
                </div>
                <Card>
                  <CardContent className="pt-6">{tab.panel}</CardContent>
                </Card>
              </div>
            ),
          }))}
        />
      </div>
    </section>
  )
}
